import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../../api/endpoints/auth";
import styles from "./Login.module.css";
import { Form, Input, Button, message } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";

interface LoginFormData {
  username: string;
  password: string;
}

const SYSTEM_ADMIN_ROLES = ["admin", "system admin", "system_admin"];
const VIEWER_ROLES = ["viewer"];
const isSystemAdmin = (role: string) =>
  SYSTEM_ADMIN_ROLES.includes(role?.toLowerCase()?.trim());
const isViewerRole = (role: string) =>
  VIEWER_ROLES.includes(role?.toLowerCase()?.trim()); // NEW

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Helper so every error rendered via antd's message carries a stable testid
  const showLoginError = (text: string) => {
    message.error(
      <span data-testid="Login-error-message">{text}</span>
    );
  };

  const onFinish = async (values: LoginFormData) => {
    setLoading(true);
    try {
      const response = await login({
        username: values.username,
        password: values.password,
      });

      sessionStorage.setItem("accessToken", response.token);
      sessionStorage.setItem("user", JSON.stringify(response.user));

      const role = response.user.role ?? "";

      if (isSystemAdmin(role)) {
        sessionStorage.removeItem("selectedCompany");
        window.dispatchEvent(new Event("companyChanged"));
        message.success(response.message || "Login successful!");
        navigate("/admin/companies");
      } else if (isViewerRole(role)) {
        const companyId = response.companyId;
        if (!companyId) {
          showLoginError("Company data missing. Contact support.");
          return;
        }
        sessionStorage.setItem(
          "selectedCompany",
          JSON.stringify({ id: companyId, name: response.user.name })
        );
        window.dispatchEvent(new Event("companyChanged"));
        message.success(response.message || "Login successful!");
        navigate("/dashboard");
      } else {
        const companyId = response.companyId;
        if (!companyId) {
          showLoginError("Company data missing. Contact support.");
          return;
        }
        sessionStorage.setItem(
          "selectedCompany",
          JSON.stringify({ id: companyId, name: response.user.name })
        );
        window.dispatchEvent(new Event("companyChanged"));
        message.success(response.message || "Login successful!");
        navigate("/dashboard");
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Invalid username or password";
      showLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${styles.fullContainer} login-page`} data-testid="login-page">
      <div className={styles.bgGradient1}></div>
      <div className={styles.bgGradient2}></div>
      <div className={styles.bgGradient3}></div>
      <div className={styles.bgPattern}></div>
      <div className={styles.decorCircle1}></div>
      <div className={styles.decorCircle2}></div>

      {/* Left Side */}
      <div className={styles.leftSide}>
        <div className={styles.brandingContent}>
          <div className={styles.logoContainer}>
            <div className={styles.logoIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <h1 className={styles.brandName}>QUDRA</h1>
          </div>
          <h2 className={styles.brandTitle}>Precision in Every Encounter</h2>
          <p className={styles.brandSubtitle}>
            Streamline your clinical and billing operations with QUDRA —
            the intelligent modular platform built for healthcare providers.
            Reduce claim rejections, accelerate reimbursements, and ensure full
            compliance with Gulf healthcare regulations.
          </p>
          <div className={styles.features}>
            {[
              "Smart Clinical & Claim Automation",
              "Modular & Scalable Design",
              "Advanced Operational Insights",
              "Built-in Gulf Market Compliance",
              "Robust Validation & Error Prevention",
            ].map((text, i) => (
              <div key={i} className={styles.feature}>
                <div className={styles.featureIcon}>✓</div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div className={styles.rightSide}>
        <div className={styles.loginCard}>
          <div className={styles.header}>
            <h2 className={styles.title}>Sign In</h2>
            <p className={styles.subtitle}>Enter your credentials to access your account</p>
          </div>
          <div className={styles.formContainer}>
            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              autoComplete="off"
              layout="vertical"
              size="large"
              requiredMark={false}
            >
              <Form.Item
                label={<span style={{ fontWeight: "bold" }}>User Name:</span>}
                name="username"
                className={styles.label}
                rules={[
                  { required: true, message: "Username is required!" },
                  { min: 3, message: "Username must be at least 3 characters!" },
                ]}
              >
                <Input
                  data-testid="Login-username-input"
                  prefix={<UserOutlined style={{ color: "#8c8c8c" }} />}
                  placeholder="Enter your username"
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontWeight: "bold" }}>Password:</span>}
                name="password"
                className={styles.label}
                rules={[
                  { required: true, message: "Please enter your password!" },
                  { min: 4, message: "Password must be at least 4 characters!" },
                ]}
              >
                <Input.Password
                  data-testid="Login-password-input"
                  prefix={<LockOutlined style={{ color: "#8c8c8c" }} />}
                  placeholder="Password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  data-testid="Login-signin-button"
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  className={styles.button}
                >
                  Sign In
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
